import Script from 'next/script'
import { useMemo } from 'react'
import { ILightRailItem, IProgramData } from '../../../types'

export default function VideoObjectJsonLd({
  program,
  asPath,
  logo,
}: {
  program: IProgramData | null
  asPath: string
  logo?: string
}) {
  const currentAsset = useMemo(
    () =>
      program?.episodes?.find(ep => ep.id == program.episode) as ILightRailItem,
    [program?.episode, program?.episodes]
  )

  const dataSource = useMemo(
    () => currentAsset || program?.programData,
    [currentAsset, program?.programData]
  )

  const duration = useMemo(() => {
    if (dataSource?.mediaFiles && dataSource.mediaFiles.length > 0) {
      return formatDuration(dataSource.mediaFiles?.[0]?.duration)
    }
    if (dataSource?.metas?.RunTime) {
      if (typeof dataSource.metas?.RunTime === 'string') {
        return formatDuration(parseInt(dataSource.metas.RunTime))
      }
      return formatDuration(parseInt(dataSource.metas.RunTime.value))
    }
    return 0
  }, [dataSource?.mediaFiles, dataSource?.metas?.RunTime])

  const removeQuotes = (input: string): string => {
    return input.replace(/"/g, '')
  }

  const jsonLd: Record<string, string | {}> | null = useMemo(() => {
    if (!dataSource) {
      return null
    }
    const replacedAsPath = asPath.replace('allshows/', 'allshows/embed/')
    const embedUrl = process.env.BASE_URL + replacedAsPath

    const { name, description, images, startDate, metas } = dataSource
    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: removeQuotes(name) || '',
      description: removeQuotes(description) || '',
      thumbnailUrl: images?.[0]?.url + 'width/1020/height/574' || '',
      uploadDate: formatTimestampToDateTime(
        startDate || metas?.CatalogStartDateTime?.value || 0
      ),
      duration: duration,
      embedUrl: embedUrl,
      contentUrl: process.env.BASE_URL + asPath,
      publisher: {
        '@type': 'Organization',
        name: 'רשת 13',
        logo: {
          '@type': 'ImageObject',
          url: logo,
        },
      },
    }
  }, [asPath, dataSource, duration, logo])

  if (!program || !dataSource) {
    return null
  }

  return (
    <Script
      id='VideoObject'
      type='application/ld+json'
      strategy='beforeInteractive'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLd),
      }}
    />
  )

  function formatTimestampToDateTime(timestamp: number): string {
    const millis = timestamp < 1e12 ? timestamp * 1000 : timestamp
    const date = new Date(millis)
    const pad = (n: number): string => n.toString().padStart(2, '0')
    const year = date.getFullYear()
    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    const hours = pad(date.getHours())
    const minutes = pad(date.getMinutes())
    const seconds = pad(date.getSeconds())
    const offset = -date.getTimezoneOffset()
    const sign = offset >= 0 ? '+' : '-'
    const offsetHours = pad(Math.floor(Math.abs(offset) / 60))
    const offsetMinutes = pad(Math.abs(offset) % 60)
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`
  }

  function formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `PT${h > 0 ? h + 'H' : ''}${m > 0 ? m + 'M' : ''}${
      s > 0 ? s + 'S' : ''
    }`
  }
}
